/**
 * Deterministic layout of one category bundle (plan section 3, "Layout"): pure, no randomness, memoised by bundle fingerprint.
 *
 * Causes are grouped into their line-of-diagnosis cluster (bundle.causes[].lods, first known line; else a General cluster).
 * Clusters sit on a ring (or a grid past LAYOUT.RING_MAX_CLUSTERS); causes inside a cluster follow a Fibonacci spiral ordered by
 * a hash of their id. Symptoms and facts live inside the cluster of the causes they mostly support (placeLeaves), at the
 * barycentre of those causes plus a hash-seeded jitter, clamped to the disc. A fixed number of collide-only passes de-overlaps
 * the result. Leaves with no cause neighbour are not placed (nothing links them to a cause).
 *
 * layoutCategory(bundle) -> {fingerprint, positions: Map<nid,{x,y}>, clusters: [{id,label,cx,cy,r}], clusterOf: Map<nid, cluster id>,
 *                            bounds, orphans: count of leaves left out}
 */
import { EDGE_KINDS, GENERAL_CLUSTER, GOLDEN_ANGLE, LAYOUT, ROLE_OF_TYPE, ROLES } from "./lightsConstants";

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const UINT32 = 0x100000000;
const GENERAL_LABEL = "General";

/** FNV-1a 32-bit hash of a string, as an unsigned integer. */
export function hashString(text) {
  let h = FNV_OFFSET;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

const hash01 = (id, salt) => hashString(`${salt}:${id}`) / UINT32;

const graphNodes = (bundle) => (bundle?.graph?.nodes || []).filter((n) => n && n.nid && ROLE_OF_TYPE[n.type]);
const graphEdges = (bundle) => (bundle?.graph?.edges || []).filter((e) => e && e.src && e.dst);
const lodDefinitions = (bundle) => (bundle?.lods || [])
  .map((d) => ({ id: d.nid || d.id, label: d.label || d.nid || d.id }))
  .filter((d) => d.id);

/** Changes whenever anything the layout reads changes: node ids, edge count, lines and cause-to-line assignments. */
export function bundleFingerprint(bundle) {
  const nids = graphNodes(bundle).map((n) => `${n.type}:${n.nid}`).sort();
  const lines = lodDefinitions(bundle).map((d) => d.id);
  const assigned = (bundle?.causes || []).map((c) => `${c.cause_key}=${(c.lods || []).join(",")}`).sort();
  const source = [bundle?.category || "", nids.join(";"), graphEdges(bundle).length, lines.join(";"), assigned.join(";")].join("|");
  return hashString(source).toString(16);
}

function clusterOfCause(bundle, knownLines) {
  const lines = new Map((bundle?.causes || []).map((c) => [c.cause_key, c.lods || []]));
  return (nid) => (lines.get(nid) || []).find((id) => knownLines.has(id)) || GENERAL_CLUSTER;
}

function groupCauses(bundle, causeIds) {
  const defs = lodDefinitions(bundle);
  const known = new Set(defs.map((d) => d.id));
  const clusterOf = clusterOfCause(bundle, known);
  const groups = new Map();
  causeIds.forEach((nid) => {
    const id = clusterOf(nid);
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push(nid);
  });
  const labelOf = new Map(defs.map((d) => [d.id, d.label]));
  const order = [...defs.map((d) => d.id), GENERAL_CLUSTER].filter((id) => groups.has(id));
  return order.map((id) => ({ id, label: labelOf.get(id) || GENERAL_LABEL, members: groups.get(id) }));
}

const clusterRadius = (count) => LAYOUT.CAUSE_SPACING * Math.sqrt(count + 1) + LAYOUT.LEAF_JITTER * 2.5;

function placeClusterCentres(groups) {
  const k = groups.length;
  const radii = groups.map((g) => clusterRadius(g.members.length));
  const maxR = Math.max(0, ...radii);
  if (k <= 1) return radii.map((r) => ({ cx: 0, cy: 0, r }));
  if (k <= LAYOUT.RING_MAX_CLUSTERS) {
    const ring = (maxR + LAYOUT.CLUSTER_GAP / 2) / Math.sin(Math.PI / k);
    return radii.map((r, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / k;
      return { cx: ring * Math.cos(angle), cy: ring * Math.sin(angle), r };
    });
  }
  const cols = Math.ceil(Math.sqrt(k));
  const rows = Math.ceil(k / cols);
  const cell = 2 * maxR + LAYOUT.CLUSTER_GAP;
  return radii.map((r, i) => ({
    cx: ((i % cols) - (cols - 1) / 2) * cell, cy: (Math.floor(i / cols) - (rows - 1) / 2) * cell, r,
  }));
}

function placeCauses(groups, centres, positions) {
  groups.forEach((group, gi) => {
    const { cx, cy } = centres[gi];
    const ordered = [...group.members].sort((a, b) => (hashString(a) - hashString(b)) || (a < b ? -1 : 1));
    ordered.forEach((nid, i) => {
      const dist = LAYOUT.CAUSE_SPACING * Math.sqrt(i + 0.5);
      const angle = i * GOLDEN_ANGLE;
      positions.set(nid, { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) });
    });
  });
}

/** {leaf nid: [cause nid]}: supports edges first; a leaf with none uses its rules_out edges. */
function leafNeighbours(edges, isCause) {
  const supports = new Map();
  const rulesOut = new Map();
  edges.forEach((e) => {
    if (!isCause(e.dst) || isCause(e.src)) return;
    const target = e.type === EDGE_KINDS.RULES_OUT ? rulesOut : supports;
    if (!target.has(e.src)) target.set(e.src, []);
    target.get(e.src).push(e.dst);
  });
  return (nid) => supports.get(nid) || rulesOut.get(nid) || [];
}

/** Keeps a point inside the cluster disc (LEAF_INSET of its radius), so no leaf sits in the gap or under the label. */
function clampToCluster(p, cluster) {
  const limit = cluster.r * LAYOUT.LEAF_INSET;
  const dx = p.x - cluster.cx;
  const dy = p.y - cluster.cy;
  const dist = Math.hypot(dx, dy);
  if (dist <= limit || dist === 0) return;
  p.x = cluster.cx + (dx / dist) * limit;
  p.y = cluster.cy + (dy / dist) * limit;
}

/**
 * The home cluster of a leaf is the line holding most of the causes it supports (ties: the lower hash), and it is placed at
 * the barycentre of those causes only, so a fact shared across lines no longer drifts into the gap between clusters.
 * Leaves with no cause neighbour (orphans) are not placed: nothing connects them to any cause, so they are not drawn.
 */
function placeLeaves(leafIds, neighboursOf, clusterOf, clusters, positions) {
  const byId = new Map(clusters.map((c) => [c.id, c]));
  leafIds.forEach((nid) => {
    const anchors = neighboursOf(nid).filter((c) => positions.has(c));
    if (!anchors.length) return;
    const votes = new Map();
    anchors.forEach((c) => { const id = clusterOf.get(c); votes.set(id, (votes.get(id) || 0) + 1); });
    const home = [...votes.entries()].sort((a, b) => (b[1] - a[1]) || (hashString(`${nid}|${a[0]}`) - hashString(`${nid}|${b[0]}`)))[0][0];
    const own = anchors.filter((c) => clusterOf.get(c) === home).map((c) => positions.get(c));
    const bx = own.reduce((s, p) => s + p.x, 0) / own.length;
    const by = own.reduce((s, p) => s + p.y, 0) / own.length;
    const angle = 2 * Math.PI * hash01(nid, "a");
    const dist = LAYOUT.LEAF_JITTER * (0.4 + 1.2 * hash01(nid, "r"));
    const p = { x: bx + dist * Math.cos(angle), y: by + dist * Math.sin(angle) };
    clampToCluster(p, byId.get(home));
    positions.set(nid, p);
    clusterOf.set(nid, home);
  });
}

/** Collide-only relaxation on a uniform grid; causes are heavier (move a fifth as much) so clusters keep their shape. */
function relax(order, roleOf, positions) {
  const cell = Math.max(...Object.values(LAYOUT.MIN_DIST));
  const items = order.map((nid) => ({ nid, p: positions.get(nid), d: LAYOUT.MIN_DIST[roleOf.get(nid)], w: roleOf.get(nid) === ROLES.CAUSE ? 0.2 : 1 }));
  for (let iter = 0; iter < LAYOUT.RELAX_ITERATIONS; iter += 1) {
    const grid = new Map();
    const keyOf = (x, y) => `${x},${y}`;
    items.forEach((it, idx) => {
      const k = keyOf(Math.floor(it.p.x / cell), Math.floor(it.p.y / cell));
      if (!grid.has(k)) grid.set(k, []);
      grid.get(k).push(idx);
    });
    items.forEach((a, ai) => {
      const gx = Math.floor(a.p.x / cell);
      const gy = Math.floor(a.p.y / cell);
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          (grid.get(keyOf(gx + dx, gy + dy)) || []).forEach((bi) => {
            if (bi <= ai) return;
            const b = items[bi];
            const min = (a.d + b.d) / 2;
            let vx = b.p.x - a.p.x;
            let vy = b.p.y - a.p.y;
            let dist = Math.hypot(vx, vy);
            if (dist >= min) return;
            if (dist === 0) {
              const angle = 2 * Math.PI * hash01(`${a.nid}|${b.nid}`, "c");
              vx = Math.cos(angle); vy = Math.sin(angle); dist = 1e-6;
            }
            const push = (min - dist) / (a.w + b.w);
            const ux = vx / (dist || 1);
            const uy = vy / (dist || 1);
            a.p.x -= ux * push * a.w; a.p.y -= uy * push * a.w;
            b.p.x += ux * push * b.w; b.p.y += uy * push * b.w;
          });
        }
      }
    });
  }
}

function boundsOf(positions) {
  const b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  positions.forEach(({ x, y }) => {
    b.minX = Math.min(b.minX, x); b.minY = Math.min(b.minY, y);
    b.maxX = Math.max(b.maxX, x); b.maxY = Math.max(b.maxY, y);
  });
  return Number.isFinite(b.minX) ? b : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
}

function computeLayout(bundle, fingerprint) {
  const nodes = graphNodes(bundle);
  const roleOf = new Map(nodes.map((n) => [n.nid, ROLE_OF_TYPE[n.type]]));
  const causeIds = nodes.filter((n) => roleOf.get(n.nid) === ROLES.CAUSE).map((n) => n.nid);
  const isCause = (nid) => roleOf.get(nid) === ROLES.CAUSE;
  const groups = groupCauses(bundle, causeIds);
  const centres = placeClusterCentres(groups);
  const clusters = groups.map((g, i) => ({ id: g.id, label: g.label, ...centres[i] }));
  const clusterOf = new Map(groups.flatMap((g) => g.members.map((nid) => [nid, g.id])));
  const positions = new Map();
  placeCauses(groups, centres, positions);
  const leafIds = nodes.filter((n) => !isCause(n.nid)).map((n) => n.nid).sort();
  placeLeaves(leafIds, leafNeighbours(graphEdges(bundle), isCause), clusterOf, clusters, positions);
  const placedLeaves = leafIds.filter((nid) => positions.has(nid));
  relax([...causeIds].sort().concat(placedLeaves), roleOf, positions);
  const byCluster = new Map(clusters.map((c) => [c.id, c]));
  placedLeaves.forEach((nid) => clampToCluster(positions.get(nid), byCluster.get(clusterOf.get(nid))));
  const bounds = boundsOf(positions);
  return { fingerprint, positions, clusters, clusterOf, bounds, orphans: leafIds.length - placedLeaves.length };
}

const memo = new Map();

/** The layout of a bundle, computed once per fingerprint (a small LRU in module scope). */
export function layoutCategory(bundle) {
  const fingerprint = bundleFingerprint(bundle);
  if (memo.has(fingerprint)) return memo.get(fingerprint);
  const layout = computeLayout(bundle, fingerprint);
  memo.set(fingerprint, layout);
  if (memo.size > LAYOUT.MEMO_SIZE) memo.delete(memo.keys().next().value);
  return layout;
}
